import { useState, useEffect, useCallback } from "react";
import { avatarBg } from "@/lib/avatar";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import Navigation from "@/components/Navigation";
import { useAppMode } from "@/context/AppModeContext";
import { Button } from "@/components/ui/button";
import { Wallet, Lock, CheckCircle2, Calendar, AlertTriangle, TrendingUp, Loader2 } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

export default function Earnings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEarner } = useAppMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEarnings = useCallback(async () => {
    try {
      const response = await api.get("/earnings");
      setData(response.data);
    } catch (error) {
      console.error("Failed to load earnings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !isEarner) {
      navigate("/discover");
      return;
    }
    fetchEarnings();
  }, [user, isEarner, navigate, fetchEarnings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="earnings-page">
      <Navigation />

      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Wallet className="w-7 h-7 text-primary" />
            <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight">My Earnings</h1>
          </div>

          {data && !data.paypal_email_set && (
            <div
              className="glass-card p-4 mb-6 border border-amber-500/40 bg-amber-500/5 flex items-start gap-3"
              data-testid="paypal-setup-warning"
            >
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-300">Add your PayPal email to get paid</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Payouts are sent directly to your PayPal. Set it up before confirming a date.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                onClick={() => navigate("/settings")}
                data-testid="setup-paypal-btn"
              >
                Set up
              </Button>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="glass-card p-4 text-center" data-testid="pending-escrow-card">
              <Lock className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
              <p className="text-xl md:text-2xl font-bold text-yellow-400">
                ${data?.pending_escrow?.toFixed(2) || "0.00"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">In Escrow</p>
            </div>
            <div className="glass-card p-4 text-center" data-testid="total-earned-card">
              <TrendingUp className="w-5 h-5 text-green-400 mx-auto mb-2" />
              <p className="text-xl md:text-2xl font-bold text-green-400">
                ${data?.total_earned?.toFixed(2) || "0.00"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Total Earned</p>
            </div>
            <div className="glass-card p-4 text-center" data-testid="dates-completed-card">
              <Calendar className="w-5 h-5 text-primary mx-auto mb-2" />
              <p className="text-xl md:text-2xl font-bold text-primary">
                {data?.dates_completed || 0}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Dates Done</p>
            </div>
          </div>

          {/* History */}
          <h2 className="text-lg font-semibold mb-4">Payment History</h2>
          {!data?.history?.length ? (
            <div className="glass-card p-12 text-center" data-testid="earnings-empty-state">
              <Wallet className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-40" />
              <h3 className="text-lg font-semibold mb-2">No earnings yet</h3>
              <p className="text-muted-foreground text-sm">
                When someone pays for a date with you, the payment appears here — held safely in escrow until you confirm the date.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.history.map((item) => (
                <div
                  key={item.offer_id}
                  className="glass-card p-4 flex items-center gap-4"
                  data-testid={`earning-item-${item.offer_id}`}
                >
                  <img
                    src={item.man?.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.man?.name || "User")}&background=${avatarBg()}&color=fff&size=100`}
                    alt={item.man?.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{item.man?.name || "Unknown"}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.status === "completed"
                        ? `Paid out • ${formatDate(item.completed_at)}`
                        : `Paid • ${formatDate(item.paid_at)}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${item.status === "completed" ? "text-green-400" : "text-yellow-400"}`}>
                      ${item.payout.toFixed(2)}
                    </p>
                    {item.status === "completed" ? (
                      item.payout_status === "manual_pending" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-blue-400" data-testid={`payout-processing-${item.offer_id}`}>
                          <Loader2 className="w-3 h-3 animate-spin" /> Processing
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-green-400">
                          <CheckCircle2 className="w-3 h-3" /> Paid out
                        </span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-yellow-400">
                        <Lock className="w-3 h-3" /> In escrow
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {data?.pending_count > 0 && (
            <div className="glass-card p-4 mt-6 border border-primary/20 bg-primary/5">
              <p className="text-sm text-muted-foreground">
                💡 You have <strong className="text-white">{data.pending_count}</strong> payment{data.pending_count > 1 ? "s" : ""} in escrow.
                After the date, enter their 6-digit code on the{" "}
                <button className="text-primary hover:underline" onClick={() => navigate("/premium-offers")}>
                  Offers page
                </button>{" "}
                to release your payout instantly.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
